import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row, Form, Select } from "antd";
import { useTranslation } from "react-i18next";
import { fetchAudioFeaturesTrend } from "radar/Service/RadarService";
import { Card, Charts } from "shared/components";
import {
  RADAR_AUDIO_FEATURE_TREND_AUDIO_FEATURE,
  RADAR_AUDIO_FEATURE_TREND_FILTER,
} from "shared/constants/LocalStorageConsts";

const initial = () => {
  const savedFilter = localStorage.getItem(RADAR_AUDIO_FEATURE_TREND_FILTER);
  const savedAudioFeature = localStorage.getItem(
    RADAR_AUDIO_FEATURE_TREND_AUDIO_FEATURE
  );
  if (savedFilter) {
    const parsed = JSON.parse(savedFilter);
    const audioFeature = JSON.parse(savedAudioFeature || "null");
    return {
      defaultFilter: parsed,
      audioFeature,
    };
  }
  return {};
};

const audioFeatureOptions = (t: any) => [
  {
    label: t("pages.AudioFeaturesTrend.danceability"),
    value: "danceability",
  },
  { label: t("pages.AudioFeaturesTrend.energy"), value: "energy" },
  { label: t("pages.AudioFeaturesTrend.speechiness"), value: "speechiness" },
  {
    label: t("pages.AudioFeaturesTrend.acousticness"),
    value: "acousticness",
  },
  {
    label: t("pages.AudioFeaturesTrend.instrumentalness"),
    value: "instrumentalness",
  },
  { label: t("pages.AudioFeaturesTrend.liveness"), value: "liveness" },
  { label: t("pages.AudioFeaturesTrend.valence"), value: "valence" },
  { label: t("pages.AudioFeaturesTrend.tempo"), value: "tempo" },
];

const AudioFeaturesTrend: React.FC<any> = (props) => {
  const { filter } = props;
  const { audioFeature } = initial();
  const [rawTrend, setRawTrend] = useState<any>({});
  const [dataIndex, setDataIndex] = useState(-1);
  const [selectedAudioFeature, setSelectedAudioFeature] =
    useState(audioFeature);
  const [trend, setTrend] = useState<any>({ series: [], categories: [] });
  const [currentAudioFeatures, setCurrentAudioFeatures] = useState<any>({
    series: [],
    categories: [],
  });
  const { t } = useTranslation("radar");
  const [selectedDate, setSelectedDate] = useState<any>();

  const onCLickChart = (e: any, chart: any, config: any) => {
    setDataIndex(config.dataPointIndex);
    setSelectedDate(rawTrend?.history[config?.dataPointIndex]?.date);
  };

  const onChangeAudioFeature = (value: string) => {
    setSelectedAudioFeature(value);
    localStorage.setItem(
      RADAR_AUDIO_FEATURE_TREND_AUDIO_FEATURE,
      JSON.stringify(value)
    );
  };

  useEffect(() => {
    const getAudioFeatureTrend = async () => {
      const { monthQuantity, size } = filter;
      if ((monthQuantity || monthQuantity === 0) && size) {
        const response: any = await fetchAudioFeaturesTrend(
          monthQuantity,
          size
        );
        setRawTrend(response);
      } else setRawTrend({});
    };

    if (filter) getAudioFeatureTrend();
  }, [filter]);

  useEffect(() => {
    const proccessTrend = () => {
      if (rawTrend?.history && selectedAudioFeature) {
        setTrend(
          rawTrend.history.reduce(
            (acc: any, curr: any) => {
              acc.categories.push(moment(curr.date).format("MMM DD, YYYY"));
              acc.series[0].data.push(curr.audioFeatures[selectedAudioFeature]);
              return acc;
            },
            {
              categories: [],
              series: [
                {
                  name: selectedAudioFeature,
                  data: [],
                },
              ],
            }
          )
        );
      }
    };
    proccessTrend();
  }, [rawTrend, selectedAudioFeature]);

  useEffect(() => {
    const proccessAudioFeatures = () => {
      if (rawTrend?.latest || (rawTrend?.history && dataIndex > -1)) {
        setCurrentAudioFeatures(
          audioFeatureOptions(t).reduce(
            (acc: any, feature: any) => {
              acc.categories.push(feature.label);
              acc.series[0].data.push(
                (dataIndex > -1 ? rawTrend.history[dataIndex] : rawTrend.latest)
                  .audioFeatures[feature.value]
              );
              return acc;
            },
            {
              series: [
                {
                  name: "current",
                  data: [],
                },
              ],
              categories: [],
            }
          )
        );
      } else {
        setCurrentAudioFeatures({
          series: [],
          categories: [],
        });

        setTrend({ series: [], categories: [] });
      }
    };
    proccessAudioFeatures();
  }, [rawTrend, dataIndex, t]);

  return (
    <Col xs={24} className="conteudo-home">
      <Row style={{ marginTop: "16px" }}>
        <Col xs={24}>
          <Card>
            <Row justify="center">
              <Col xs={6}>
                <Form.Item label={t("pages.AudioFeaturesTrend.audioFeature")}>
                  <Select
                    options={audioFeatureOptions(t)}
                    allowClear
                    onChange={onChangeAudioFeature}
                    placeholder={t("pages.ArtistsRanking.selectHere")}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={16}>
                <Charts.LineChart
                  titulo={t("pages.AudioFeaturesTrend.chartTitle")}
                  {...trend}
                  xAxisFormatter={(val) => (val || 0).toPrecision(3)}
                  onClick={onCLickChart}
                  color={[
                    "#2E90FA",
                    "#32D583",
                    "#ADD55D",
                    "#B692F6",
                    "#75D1D2",
                    "#E4A010",
                    "#F05576",
                    "#FF7514",
                    "#ED955E",
                    "#7D7F7D",
                    "#9762D3",
                    "#EB5D53",
                  ]}
                  xaxisLabelFormat="MM/yy"
                  xaxisType="datetime"
                  height={500}
                />
              </Col>
              <Col
                xs={8}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={{ width: "100%" }}>
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      fontSize: "18px",
                      color: "white",
                    }}
                  >
                    {selectedDate
                      ? selectedDate
                      : t("pages.AudioFeaturesTrend.latest")}
                  </span>
                  <Charts.RadarChart
                    {...currentAudioFeatures}
                    width={90}
                    legend={false}
                    showTooltip={false}
                    useI18nLabel={false}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Col>
  );
};

export default AudioFeaturesTrend;
